import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import DesktopGallery from './gallery/DesktopGallery';
import MobileCameraRoll from './gallery/MobileCameraRoll';
import GalleryLogin from './gallery/GalleryLogin';
import ProtectedGallery from './gallery/ProtectedGallery';
import HFBAdminPanel from './gallery/hfb-admin-panel';
import HomePage from './components/HomePage';
import ContactPage from './components/ContactPage';
// import UploadPage from './components/UploadPage'; // OLD VERSION - COMMENTED OUT
import UploadNotePage from './components/UploadNotePage'; // NEW VERSION WITH EXAMPLE3 DESIGN
import PricingPage from './components/PricingPage';
import HowItWorksPage from './components/HowItWorksPage';
import LinktreePage from './components/LinktreePage';
import './App.css';

// Create an axios instance with custom config
const axiosInstance = axios.create({
  timeout: 85000, // 85 seconds - slightly less than Heroku's 90s limit
  headers: {
    'Connection': 'keep-alive'
  }
});

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

function App() {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);

  // Function to send keep-alive request
  const sendKeepAlive = async () => {
    try {
      await axiosInstance.get('/api/media/keep-alive', { timeout: 5000 });
      
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Keep-alive ping failed:`, error);
      setUploadError('Bağlantı sorunu oluştu. Lütfen sayfayı yenileyip tekrar deneyin.');
    }
  };

  const uploadChunk = async (file, start, end, attempt = 1) => {
    const chunk = file.slice(start, end);
    const chunkForm = new FormData();
    chunkForm.append('chunk', chunk);
    chunkForm.append('fileName', file.name);
    chunkForm.append('start', start);
    chunkForm.append('end', end);
    chunkForm.append('total', file.size);
    chunkForm.append('mimeType', file.type);

    try {
      const response = await axiosInstance.post('/api/media/upload-chunk', chunkForm, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const overallProgress = Math.round(((start + (progressEvent.loaded * (end - start) / progressEvent.total)) * 100) / file.size);
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: overallProgress
          }));
          
        }
      });
      return response.data;
    } catch (error) {
      if (attempt < 3) {
        
        return uploadChunk(file, start, end, attempt + 1);
      }
      throw error;
    }
  };

  const uploadLargeFile = async (file) => {
    const chunks = Math.ceil(file.size / CHUNK_SIZE);
    

    try {
      for (let i = 0; i < chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        await uploadChunk(file, start, end);
      }

      // Final completion request
      const response = await axiosInstance.post('/api/media/complete-upload', {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      });

      return response.data;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Error in chunked upload for ${file.name}:`, error);
      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress({});
    const failedUploads = [];
    const successfulUploads = [];

    // Start keep-alive interval
    keepAliveIntervalRef.current = setInterval(sendKeepAlive, 15000);

    // Sequential upload for files
    for (const file of files) {
      try {
        

        let response;
        if (file.size > CHUNK_SIZE) {
          // Use chunked upload for large files
          response = await uploadLargeFile(file);
        } else {
          // Use regular upload for small files
          const formData = new FormData();
          formData.append('media', file);
          const uploadResponse = await axiosInstance.post('/api/media/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({
                ...prev,
                [file.name]: percentCompleted
              }));
              
            }
          });
          response = uploadResponse.data;
        }

        if (response.success || (response.data && response.data.success)) {
          console.log(`[${new Date().toISOString()}] Successfully uploaded: ${file.name}`);
          successfulUploads.push(response.data || response);
        } else {
          console.error(`[${new Date().toISOString()}] Upload failed for ${file.name}: Server returned unsuccessful response`);
          failedUploads.push(file.name);
        }
      } catch (error) {
        console.error(`[${new Date().toISOString()}] Error uploading ${file.name}:`, error);
        let errorMessage = `${file.name}: `;
        if (error.code === 'ECONNABORTED') {
          errorMessage += 'Bağlantı zaman aşımına uğradı';
        } else if (error.response?.status === 413) {
          errorMessage += 'Dosya boyutu çok büyük';
        } else {
          errorMessage += 'Yükleme başarısız oldu';
        }
        failedUploads.push(errorMessage);
        setUploadError('Yükleme sırasında hata oluştu. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.');
      }
    }

    // Clear keep-alive interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }

    setUploadedFiles(prev => [...prev, ...successfulUploads]);
    
    if (failedUploads.length > 0) {
      const failedNames = failedUploads.join('\n');
      setUploadError(`Bazı dosyalar yüklenemedi:\n${failedNames}\nLütfen tekrar deneyin veya dosya boyutunu kontrol edin.`);
    }
    
    setUploading(false);
    setUploadProgress({});
    e.target.value = null; // Reset file input
  };

  // Clean up interval on component unmount
  React.useEffect(() => {
    return () => {
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
    };
  }, []);

  return (
    <Router>
      <Routes>
        {/* Gallery Login Routes */}
        <Route path="/camera-roll/login" element={<GalleryLogin />} />
        <Route path="/camera-roll-mobile/login" element={<GalleryLogin />} />
        
        {/* Protected Gallery Routes (authenticated via cookie) */}
        <Route path="/camera-roll" element={<ProtectedGallery />} />
        <Route path="/camera-roll-mobile" element={<ProtectedGallery />} />
        
        {/* Legacy Routes (direct hash access - kept for backwards compatibility) */}
        <Route path="/camera-roll/:hash" element={<DesktopGallery />} />
        <Route path="/camera-roll-mobile/:hash" element={<MobileCameraRoll />} />
        
        <Route path="/hfb-888-admin-25" element={<HFBAdminPanel />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/links" element={<LinktreePage />} />
        {/* <Route path="/:uploadPath" element={<UploadPage />} /> */} {/* OLD VERSION - COMMENTED OUT */}
        <Route path="/:uploadPath" element={<UploadNotePage />} /> {/* NEW VERSION WITH EXAMPLE3 DESIGN */}
      </Routes>
    </Router>
  );
}

export default App; 
